import { LitElement, html, TemplateResult } from "lit";
import {
  property,
  customElement,
  state,
  queryAssignedNodes,
} from "lit/decorators.js";
import { TEXTFIELD_TYPEAHEAD_ITEM_CLICK } from "./textfield-typeahead-item.constants";
import { style } from "./assets/textfield-typeahead-item.generated-style";

/**
 * Datatable filter textfieled typeahead item
 *
 * @cssprop --slh-default-font-family - Font family.
 * @cssprop --slh-base-semi-bold-font-weight - Font weight.
 * @cssprop --slh-base-xxs-font-size - Font size.
 * @cssprop --slh-base-xxs-line-height - Line height.
 * @cssprop --slh-color-neutral-shade-80 - Font color.
 *
 */
@customElement("slh-textfield-typeahead-item")
export default class SlhTextfieldTypeaheadItem extends LitElement {
  /**
   * Used for setting id attribute value to the element, if not already set
   * @private
   */
  static instanceCount = 0;

  @state()
  protected selectedState: boolean = false;

  @state()
  protected textState: string = "";

  @queryAssignedNodes()
  protected defaultSlotNodes: Array<HTMLElement> | undefined;

  static styles = style;

  /**
   * Item value
   */
  @property({
    type: String,
    attribute: true,
    reflect: true,
  })
  value: string | number = "";

  /**
   * Item text
   */
  @property({
    type: String,
    attribute: true,
    reflect: true,
  })
  text: string | number = "";

  /**
   * @fires "click"
   */
  emitClickEvent() {
    const customEvent = new CustomEvent(TEXTFIELD_TYPEAHEAD_ITEM_CLICK, {
      detail: {
        text: this.text,
        value: this.value,
        selected: this.selectedState,
        id: this.id,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(customEvent);
  }

  /**
   * @returns {string}
   */
  getLabel() {
    return this.textState;
  }

  /**
   * @returns {string} render html template
   */
  render(): TemplateResult {
    return html`<div
      @click="${this.emitClickEvent}"
      class="slh-textfield-typeahead-item"
    >
      <slot></slot>
    </div>`;
  }

  /**
   * Called after the component's DOM has been updated the first time, immediately before updated() is called.
   * Set text state, and update value if it's not defined
   */
  firstUpdated() {
    if (
      this.defaultSlotNodes !== undefined &&
      this.defaultSlotNodes.length > 0 &&
      typeof this.defaultSlotNodes[0].textContent === "string"
    ) {
      this.textState = this.defaultSlotNodes[0].textContent;
      this.setAttribute("aria-label", this.textState.trim());
    } else {
      // eslint-disable-next-line no-console
      console.error(
        "[slh-textfield-typeahead-item] You must provide text slot to component"
      );
    }

    if (!this.hasAttribute("id")) {
      this.id = `slh-textfield-typeahead-item-${(SlhTextfieldTypeaheadItem.instanceCount += 1)}`;
    }
    this.setAttribute("role", "option");
    this.setAttribute("tabindex", "-1");

    if (this.selectedState) {
      this.setAttribute("aria-selected", "true");
    }
  }
}
